// The homepage Category rail: the category buckets plus the 2 content-derived offerings
// (Games / Hobbies). Admins can rename / reorder / hide these via /admin/categories, stored
// at config/categoryRail and served by the getCategoryRail callable. Keys are FIXED — this
// is an override layer over the defaults below, never a way to add/remove buckets.
//
// GAMES AND HOBBIES ARE PRESENTED SEPARATELY, so the combined 'hobbies' bucket
// ("Hobbies & Games") is deliberately NOT railed. Showing all three put "Hobbies & Games (0)"
// next to "⚽ Games (6)" and "🎨 Hobbies (4)" on the live homepage: the bucket counts mentors
// who PICKED that track (nobody had), while the offerings count what mentors actually teach.
// Two representations of one thing, with contradictory numbers.
//
// This is presentation only — the 'hobbies' category slug still exists in storage, in TRACKS
// and in the server allow-lists, so no mentor data moves and nothing needs migrating. Such a
// mentor still surfaces under Games / Hobbies, which is derived from the very arrays that
// track writes (advertising.sportsTaught / advertising.hobbies).
import { MENTOR_CATEGORIES, MENTOR_CATEGORY_LABEL, OFFERINGS, OFFERING_LABEL } from './mentorFilterState';

export interface RailEntry { key: string; label: string; hidden: boolean }

// Canonical order + default labels. Category keys are the raw slug; offerings are 'off:*',
// matching the keys Home/MentorFilters use to route to filters.category vs filters.offering.
const RAILED_CATEGORIES = MENTOR_CATEGORIES.filter((c) => c !== 'hobbies');

export const DEFAULT_RAIL: RailEntry[] = [
  ...RAILED_CATEGORIES.map((c) => ({ key: c as string, label: MENTOR_CATEGORY_LABEL[c], hidden: false })),
  ...OFFERINGS.map((o) => ({ key: `off:${o}`, label: OFFERING_LABEL[o], hidden: false })),
];

export const ALLOWED_RAIL_KEYS = DEFAULT_RAIL.map((e) => e.key);

/**
 * Merge stored admin overrides over the defaults: honour the stored order + label + hidden
 * for known keys, drop unknown keys, and append any canonical key the config is missing (so
 * a newly-added-in-code bucket still shows until an admin arranges it). Passing [] → defaults.
 */
export function resolveRail(stored: readonly RailEntry[] | null | undefined): RailEntry[] {
  const byKey = new Map(DEFAULT_RAIL.map((e) => [e.key, e]));
  const out: RailEntry[] = [];
  const used = new Set<string>();
  for (const e of stored ?? []) {
    if (!byKey.has(e.key) || used.has(e.key)) continue;
    used.add(e.key);
    out.push({ key: e.key, label: e.label || byKey.get(e.key)!.label, hidden: e.hidden === true });
  }
  for (const def of DEFAULT_RAIL) if (!used.has(def.key)) out.push(def);
  return out;
}
